import { get, query, orderByChild, equalTo, ref } from 'firebase/database'
import { database } from '@/firebase/config'
import { formatDate } from '@/utils/dateFormatter'
import JSZip from 'jszip'

/**
 * Obtiene la ruta completa para una colección o documento de una institución
 * @param basePath Ruta base (ej: 'institutions', 'users', etc)
 * @param institutionId ID de la institución
 * @param testMode Indica si estamos en modo prueba
 * @returns Ruta completa formateada
 */
function getInstitutionPath(basePath: string, institutionId: string, testMode: boolean = false): string {
	const prefix = testMode ? '/testMode' : ''
	return `/institutions/${institutionId}${prefix}${basePath}`
}

/**
 * Interfaz para los documentos que pueden ser descargados
 */
export interface DownloadableDocument {
	id: string
	folioControl?: string
	xml?: string
	cadenaOriginal?: string | Record<string, string>
	signed?: boolean
	date?: string | number
	package?: number
}

/**
 * Interfaz para los paquetes de documentos
 */
export interface DownloadablePackage {
	id: number
	label: string
	signed: boolean
	responsables?: {
		label: string
		name: string
	}[]
}

export interface Package {
	id: number
	name: string
	date: string
	signed: boolean
	sent: boolean
	numDegrees: number
	responsables: Record<string, string>
	label: string
}

/**
 * Servicio para manejar la descarga de documentos
 */
export class DownloadService {
	/**
	 * Descarga un documento individual
	 * @param doc El documento a descargar
	 * @param documentType El tipo de documento (degree, certificate, etc.)
	 * @returns Promise<void>
	 */
	async downloadDocument(doc: DownloadableDocument, documentType: string = 'document'): Promise<void> {
		try {
			// Crear un archivo zip
			const zip = new JSZip()

			// Agregar cadena original
			if (doc.cadenaOriginal) {
				if (typeof doc.cadenaOriginal === 'object') {
					// Si es un objeto, agregar cada cadena con un sufijo
					Object.entries(doc.cadenaOriginal).forEach(([key, value]) => {
						zip.file(`${doc.folioControl}_${key}.txt`, value)
					})
				} else {
					// Si es un string, agregarlo directamente
					zip.file(`${doc.folioControl}.txt`, doc.cadenaOriginal)
				}
			}

			// Agregar XML
			if (doc.xml) {
				zip.file(`${doc.folioControl}.xml`, doc.xml)
			}

			// Generar y descargar zip
			const content = await zip.generateAsync({ type: 'blob' })
			const url = window.URL.createObjectURL(content)
			const a = document.createElement('a')
			a.href = url
			doc.date = typeof doc.date === 'number' ? formatDate(new Date(doc.date)) : doc.date
			a.download = `${doc.folioControl}_${formatDate(doc.date ?? new Date().toISOString(), 'YYYY-MM-DD HH:mm')}.zip`
			document.body.appendChild(a)
			a.click()
			window.URL.revokeObjectURL(url)
			document.body.removeChild(a)
		} catch (error) {
			console.error(`Error downloading ${documentType}:`, error)
			throw new Error(`No fue posible descargar el ${documentType}`)
		}
	}

	/**
	 * Descarga un paquete completo con sus cadenas originales y XMLs
	 * @param packageId ID del paquete a descargar
	 * @param institutionId ID de la institución
	 * @param testMode Si está en modo prueba
	 */
	async downloadPackage({ packageId, institutionId, packageLabel, testMode = false }: { packageId: number; institutionId: string; packageLabel: string; testMode?: boolean }): Promise<void> {
		try {
			// Obtener los títulos del paquete filtrados directamente por packageId
			const degreesRef = ref(database, getInstitutionPath(`/degrees/queue`, institutionId, testMode))
			const packageQuery = query(degreesRef, orderByChild('package'), equalTo(packageId))
			const snapshot = await get(packageQuery)

			if (!snapshot.exists()) {
				throw new Error('No se encontraron títulos en el paquete')
			}

			const degrees = snapshot.val()
			const zip = new JSZip()

			// Procesar cada título del paquete
			for (const [_, degree] of Object.entries(degrees)) {
				_
				const doc = degree as DownloadableDocument
				const name = doc.folioControl || ''

				// Agregar cadena original
				if (doc.cadenaOriginal) {
					if (typeof doc.cadenaOriginal === 'object') {
						Object.entries(doc.cadenaOriginal).forEach(([key, value]) => {
							if (value) zip.file(`COs/${name}_${key}.txt`, value)
						})
					} else {
						zip.file(`COs/${name}.txt`, doc.cadenaOriginal)
					}
				}

				// Agregar XML y verificar firmas
				if (doc.xml) {
					zip.file(`XMLs/${name}.xml`, doc.xml)

					const parser = new DOMParser()
					const xmlDoc = parser.parseFromString(doc.xml, 'text/xml')
					const signers = xmlDoc.getElementsByTagName('FirmaResponsables')[0]?.getElementsByTagName('FirmaResponsable')

					if (!signers || Array.from(signers).some(signer => !signer.getAttribute('sello'))) {
						throw new Error('El paquete no tiene todas las firmas necesarias')
					}
				}
			}

			// Generar y descargar el ZIP
			const content = await zip.generateAsync({ type: 'base64' })
			const link = document.createElement('a')
			const fileName = `${packageLabel}.zip`
			link.download = fileName
			link.href = `data:application/zip;base64,${content}`
			link.click()
		} catch (error) {
			console.error('Error al descargar el paquete:', error)
			throw error
		}
	}
}

// Exportar una instancia única del servicio
export const downloadService = new DownloadService()
